import React from "react";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import moment from "moment-timezone";
import { FourOhFour } from "./features/404/404";
import { useAppController } from "./useAppController";
import { Settings } from "./features/settings";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Dashboard } from "./features/dashboard/Dashboard";
import { Admin } from "./features/admin/Admin";
import { Login, Register, ResetPassword } from "./features/authentication";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { Client } from "./features/client";
import { FloatingGettingStartedProvider } from "./features/getting-started-guide/FloatingGettingStartedProvider";

import { AppShell } from "./features/app-shell/AppShell";
import { ThemeShowcase } from "./components/ThemeShowcase";
import { ThemeFonts } from "./components/ThemeFonts";
import { ApiEnvironmentSwitcher } from "./components/ApiEnvironmentSwitcher";
import { ImportsView } from "./features/imports/imports-view/ImportsView";
import { ImportFile } from "./features/imports/import-file/ImportFile";
import { AddOrUpdateTemplate } from "./features/imports/add-or-update-template/AddOrUpdateTemplate";
import { Funds } from "./features/funds";
import { Investors } from "./features/investors/Investors";
import { ChartOfAccounts } from "./features/chart-of-accounts/ChartOfAccounts";
import { Instruments } from "./features/instruments/Instruments";
import FundCalendarsView from "./features/fund-calendars/FundCalendarsView";
import { Transactions } from "./features/transactions/Transactions";
import { Holdings, HoldingsComparison } from "./features/holdings";
import { ReportingPeriodsView } from "./features/reporting-periods";
import { ExceptionsView } from "./features/exceptions";
import { ReportsView } from "./features/reports";
import { FundDashboard } from "./features/fund-dashboard/FundDashboard";
import { FundValidationRulesView } from "./features/fund-validation-rules";
import { ExplainerPage } from "./features/explainer";

/**
 * Loading component displayed during suspense
 */
const Loading = () => (
  <div className="h-screen flex items-center justify-center flex-col">
    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
    <h2 className="text-xl font-semibold mt-2">Loading...</h2>
  </div>
);

/**
 * Main App component that sets up routing and global providers
 */
export const App = () => {
  const controller = useAppController();
  moment.tz.setDefault("Europe/London");

  const queryClient = new QueryClient();

  return (
    <ThemeProvider attribute="class" defaultTheme="dark">
      <ThemeFonts />
      <Suspense fallback={<Loading />}>
        <QueryClientProvider client={queryClient}>
            <Toaster position="top-right" />
            {controller.initialised && (
              <BrowserRouter>
                <FloatingGettingStartedProvider 
                  excludeRoutes={["/app/login", "/app/register", "/app/reset", "/"]}
                  autoShowDelay={3000}
                >
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/app/login" element={<Login />} />
                <Route path="/app/register" element={<Register />} />
                <Route path="/app/reset" element={<ResetPassword />} />
                <Route path="/app" element={<AppShell />}>
                  <Route index path="/app/dashboard" element={<Dashboard />} />
                  <Route
                    index
                    path="/app/fund-dashboard"
                    element={<FundDashboard />}
                  />
                  <Route path="/app/clients" element={<Client />} />
                  <Route path="/app/settings" element={<Settings />} />
                  <Route path="/app/theme" element={<ThemeShowcase />} />
                  <Route path="/app/admin" element={<Admin />} />
                  <Route path="/app/investors" element={<Investors />} />
                  <Route path="/app/instruments" element={<Instruments />} />
                  <Route path="/app/funds" element={<Funds />} />
                  <Route
                    path="/app/chart-of-accounts"
                    element={<ChartOfAccounts />}
                  />
                  <Route
                    path="/app/fund-calendars"
                    element={<FundCalendarsView />}
                  />
                  <Route path="/app/transactions" element={<Transactions />} />
                  <Route path="/app/holdings" element={<Holdings />} />
                  <Route path="/app/holdings/comparison" element={<HoldingsComparison />} />
                  <Route path="/app/explainer" element={<ExplainerPage />} />
                  <Route path="/app/exceptions" element={<ExceptionsView />} />
                  <Route path="/app/reports" element={<ReportsView />} />
                  <Route path="/app/fund-validation-rules" element={<FundValidationRulesView />} />
                  <Route path="/app/imports" element={<ImportsView />} />
                  <Route path="/app/imports/new" element={<ImportFile />} />
                  <Route
                    path="/app/reporting-periods"
                    element={<ReportingPeriodsView />}
                  />
                  <Route
                    path="/app/imports/add-or-update-template"
                    element={<AddOrUpdateTemplate context="imports" />}
                  />

                  <Route path="*" element={<FourOhFour />} />
                </Route>
                <Route path="/404" element={<FourOhFour />} />
                <Route path="*" element={<FourOhFour />} />
              </Routes>

              {/* API Environment Switcher - only shown in local environments */}
              <ApiEnvironmentSwitcher />
                </FloatingGettingStartedProvider>
            </BrowserRouter>
          )}
        </QueryClientProvider>
      </Suspense>
    </ThemeProvider>
  );
};
